import { Controller } from "@hotwired/stimulus"

/**
 * Phase 2.8 Tier 2 — formula / bucket column modal.
 *
 * Manages the "+ Formula Column" and "+ Bucket Field" modals. On
 * submit, dispatches a `formula-column:added` event with the spec the
 * parent report-builder controller appends to `formula_columns`.
 *
 * Two kinds:
 *   - formula  Dentaku expression evaluated per row
 *   - bucket   Range-based categorical mapping (Salesforce Bucket Field)
 *
 * Targets:
 *   - modal    the modal element
 *   - kind     hidden input flagging "formula" or "bucket"
 *   - key      column key input (slug-form)
 *   - label    display label
 *   - expression  the Dentaku expression (formula only)
 *   - sourceColumn  source column key (bucket only)
 *   - rangeList  the bucket-range editor container
 */
export default class extends Controller {
  static targets = [
    "modal", "kind", "key", "label", "dataType",
    "expression", "expressionGroup",
    "sourceColumn", "rangeList", "bucketGroup", "defaultLabel",
    "error"
  ]

  static values = {
    columns: { type: Array, default: [] }
  }

  connect() {
    this._ranges = []
  }

  open(event) {
    const kind = (event.currentTarget.dataset.formulaKind || "formula")
    this.element.dataset.editing = ""
    this._kind = kind
    if (this.hasKindTarget) this.kindTarget.value = kind
    if (this.hasKeyTarget) this.keyTarget.value = ""
    if (this.hasLabelTarget) this.labelTarget.value = ""
    if (this.hasExpressionTarget) this.expressionTarget.value = ""
    if (this.hasSourceColumnTarget) this.sourceColumnTarget.value = ""
    this._ranges = []
    this._renderRanges()
    this._toggleSections()
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden")
  }

  close() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _toggleSections() {
    if (this.hasExpressionGroupTarget) {
      this.expressionGroupTarget.classList.toggle("hidden", this._kind !== "formula")
    }
    if (this.hasBucketGroupTarget) {
      this.bucketGroupTarget.classList.toggle("hidden", this._kind !== "bucket")
    }
  }

  addRange() {
    this._ranges.push({ from: 0, to: 0, label: "" })
    this._renderRanges()
  }

  removeRange(event) {
    const idx = parseInt(event.currentTarget.dataset.rangeIndex, 10)
    if (Number.isNaN(idx)) return
    this._ranges.splice(idx, 1)
    this._renderRanges()
  }

  updateRange(event) {
    const el = event.currentTarget
    const idx = parseInt(el.dataset.rangeIndex, 10)
    const field = el.dataset.field
    if (Number.isNaN(idx) || !field) return
    let value = el.value
    if (field === "from" || field === "to") value = el.value === "" ? null : Number(el.value)
    this._ranges[idx][field] = value
  }

  _renderRanges() {
    if (!this.hasRangeListTarget) return
    if (this._ranges.length === 0) {
      this.rangeListTarget.innerHTML =
        `<p class="text-[12px] text-gray-400 italic">No ranges yet.</p>`
      return
    }
    this.rangeListTarget.innerHTML = this._ranges.map((r, i) => `
      <div class="flex items-center gap-1 mb-1">
        <input type="number"
               class="w-16 text-[13px] border border-gray-200 rounded px-1.5 py-1"
               value="${r.from ?? ""}"
               placeholder="from"
               data-action="input->report-formula-column#updateRange"
               data-range-index="${i}"
               data-field="from" />
        <span class="text-gray-400">–</span>
        <input type="number"
               class="w-16 text-[13px] border border-gray-200 rounded px-1.5 py-1"
               value="${r.to ?? ""}"
               placeholder="to"
               data-action="input->report-formula-column#updateRange"
               data-range-index="${i}"
               data-field="to" />
        <input type="text"
               class="flex-1 text-[13px] border border-gray-200 rounded px-1.5 py-1"
               value="${r.label || ""}"
               placeholder="label (e.g. Critical)"
               data-action="input->report-formula-column#updateRange"
               data-range-index="${i}"
               data-field="label" />
        <button type="button"
                class="text-gray-400 hover:text-rose-600 px-1"
                data-action="click->report-formula-column#removeRange"
                data-range-index="${i}">×</button>
      </div>`).join("")
  }

  save(event) {
    event.preventDefault()
    const key = this._slug(this.keyTarget?.value || this.labelTarget?.value || "")
    const label = (this.labelTarget?.value || key).toString()
    const dataType = (this.hasDataTypeTarget ? this.dataTypeTarget.value : "number")
    const errors = []
    if (!key) errors.push("Provide a column label.")

    let spec
    if (this._kind === "formula") {
      const expression = (this.expressionTarget?.value || "").trim()
      if (!expression) errors.push("Provide an expression.")
      spec = { kind: "formula", key, label, data_type: dataType, expression }
    } else {
      const sourceColumnKey = (this.sourceColumnTarget?.value || "").trim()
      if (!sourceColumnKey) errors.push("Pick a source column.")
      if (this._ranges.length === 0) errors.push("Add at least one range.")
      spec = {
        kind: "bucket", key, label,
        source_column_key: sourceColumnKey,
        ranges: this._ranges.map(r => ({ from: r.from, to: r.to, label: r.label })),
        default_label: this.hasDefaultLabelTarget ? this.defaultLabelTarget.value : "Other"
      }
    }

    if (errors.length) {
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = errors[0]
        this.errorTarget.classList.remove("hidden")
      }
      return
    }
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")

    this.element.dispatchEvent(new CustomEvent("formula-column:added", {
      bubbles: true,
      detail: { spec }
    }))
    this.close()
  }

  _slug(value) {
    return String(value || "").toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")
  }
}
